import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import Avatar from "../../assets/svg/avatar.svg";
import daburLogo from "../../assets/svg/appIcon.svg";
import InfiniteScroll from "react-infinite-scroll-component";
import Markdown from "markdown-to-jsx";
import classNames from "classnames";
import {
  createDeepSearchChat,
  getContentHistory,
  getDeepSearchChatStatus,
  getDocumentSessionId,
  setChatSessionId,
  setPage,
  setQueryMessage,
  setSelectedChat,
} from "../../lib/redux/actions";
import TextInput from "../ui/TextInput";
import { useDispatch, useSelector } from "react-redux";
import SkeletonLoader from "../../lib/common/components/loader/SkeletonLoader";
import { CustomLink, preprocessText } from "../../lib/utils/helper";
import WelcomeInterface from "./WelcomeInterface";
import FileCard from "./FileCard";
import { Mic, ChevronDown } from "lucide-react";
import { useLocation } from "react-router-dom";
import CopyToClipboard from "../../lib/common/components/CopyToClipboard";

import { deepSearchPreprocessText } from "../../lib/utils/deep-search-helper";
import DeepSearchBotMarkdown from "../../lib/common/components/markdown/DeepSearchBotMarkdown";

import RotatingTextLoader from "../../lib/common/components/loader/RotatingTextLoader";
import PropTypes from "prop-types";
import { trackEvent, trackException } from "../../lib/utils/telemetryService";
import { MESSAGES } from "../../config/messages";

export default function DeepSearchChatInterface({
  isMobile,
  sidebarOpen,
  setChats,
  chats,
}) {
  const location = useLocation();
  const textRef = useRef("");
  const scrollRef = useRef(null);
  const isFetchingRef = useRef(false);

  const dispatch = useDispatch();
  const { selectedChat, page, sessionId, queryMessage } = useSelector(
    (state) => state.chat
  );

  const textDelayTimeout = useRef(null);
  const [displayedText, setDisplayedText] = useState("");
  const [isContentRendering, setIsContentRendering] = useState(false);
  const [tempId, setTempId] = useState(null);
  const inputRef = useRef(null);
  const [inputFocused, setInputFocused] = useState(false);
  const [inputText, setInputText] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasDocument, setHasDocument] = useState(false);

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollHeightRef = useRef(0);
  const scrollTimeoutRef = useRef(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);

  useEffect(() => {
    handleGetContentHistory();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetContentHistory = async (pageNumber = 1) => {
    if (isFetchingRef.current) return;

    try {
      isFetchingRef.current = true;
      setIsLoadingMore(true);
      if (pageNumber === 1) setIsLoadingHistory(true);

      // Store current scroll height before loading new data
      if (scrollRef.current) {
        scrollHeightRef.current = scrollRef.current.scrollHeight;
      }

      const data = {
        page: pageNumber,
        limit: 10,
        chatType: 5, // AUDIO type
      };

      const res = await getContentHistory(data);
      const newChats = res.data.data || [];

      // Filter out duplicate entries (where questionId is null)
      const filteredChats = newChats.filter((chat) => chat.questionId !== null);

      const existingIds = new Set(chats.map((chat) => chat._id));
      const uniqueNewChats = filteredChats.filter(
        (chat) => !existingIds.has(chat._id)
      );

      const processedMessages = uniqueNewChats.flatMap((chat) => [
        {
          role: "user",
          message: chat.questionId.message,
          _id: chat.questionId._id,
          type: "text",
        },
        {
          role: "assistant",
          message: chat?.deepSearch?.output || "No response received.",
          _id: `bot-${Date.now()}`,
          sessionId: chat?.sessionId,
          isAudio: chat?.isAudio || false,
          isDownloadable: chat?.isDownloadble || false,
          fileDownloadLink: chat?.outputUrl || "",
          annotations: chat?.deepSearch?.annotations,
          visitedURLs: chat?.deepSearch?.visitedURLs,
        },
      ]);

      setChats((prev) => [...processedMessages, ...prev]);

      setHasMore(processedMessages.length > 0);

      // After state update, adjust scroll position
      setTimeout(() => {
        if (scrollRef.current) {
          const newScrollHeight = scrollRef.current.scrollHeight;
          const prevScrollTop = scrollRef.current.scrollTop;
          const scrollDiff = newScrollHeight - scrollHeightRef.current;
          scrollRef.current.scrollTop = prevScrollTop + scrollDiff;
        }
      }, 0);
    } catch (error) {
      console.error(error);
      trackException(error);
    } finally {
      isFetchingRef.current = false;
      setIsLoadingMore(false);
      if (pageNumber === 1) setIsLoadingHistory(false);
    }
  };

  const fetchMoreData = async () => {
    if (isFetchingRef.current || !hasMore || isLoadingMore) return;

    // Only fetch more if we're near the top
    if (scrollRef.current && scrollRef.current.scrollTop > 100) return;

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    setIsLoadingMore(true);

    // Set a new timeout
    scrollTimeoutRef.current = setTimeout(async () => {
      const nextPage = page + 1;
      setPage(nextPage);
      await handleGetContentHistory(nextPage);
    }, 300); // 300ms debounce
  };

  const handleTextDelay = useCallback((msg) => {
    // Clear any existing timeouts
    if (textDelayTimeout.current) {
      clearTimeout(textDelayTimeout.current);
    }

    setIsContentRendering(true);
    let currentIndex = 0;
    const typingSpeed = 2;
    textRef.current = ""; // Use ref instead of state for intermediate values

    const typeNextCharacter = () => {
      if (currentIndex < msg.length) {
        textRef.current = msg.substring(0, currentIndex + 1);
        setDisplayedText(textRef.current);
        currentIndex += 1;
        textDelayTimeout.current = setTimeout(typeNextCharacter, typingSpeed);
      } else {
        setIsContentRendering(false);
      }
    };

    typeNextCharacter();

    // Cleanup function
    return () => {
      if (textDelayTimeout.current) {
        clearTimeout(textDelayTimeout.current);
      }
      setIsContentRendering(false);
    };
  }, []);

  useLayoutEffect(() => {
    if (location.pathname === "/deep-search") {
      dispatch(setSelectedChat({}));
      setChats([]);
      setHasDocument(false);
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  const getDoc = async (id) => {
    const res = await getDocumentSessionId(id);

    setHasDocument(res?.data?.document?.length > 0);
  };

  useEffect(() => {
    if (selectedChat?._id) {
      getDoc(selectedChat?._id);
    } else {
      setHasDocument(false);
    }
  }, [selectedChat?._id]);

  useEffect(() => {
    if (
      loading &&
      queryMessage &&
      sessionId &&
      sessionId === selectedChat?._id
    ) {
      setChats((prev) => [...prev, queryMessage]);
    }
    // eslint-disable-next-line
  }, [selectedChat]);

  useEffect(() => {
    if (
      location.pathname !== "/deep-search" &&
      selectedChat?._id &&
      (chats?.length === 0 ||
        (chats[chats.length - 1]?._id &&
          !chats[chats.length - 1]._id.includes("temp-user-")))
    ) {
      // Clear existing chats when selecting a new chat
      setChats([]);
      setHasDocument(false);
      handleGetContentHistory(1);
    }
    // eslint-disable-next-line
  }, [selectedChat]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.scrollLeft = inputRef.current.scrollWidth;
    }
  }, [inputText]);

  useEffect(() => {
    if (chats.length > 0) {
      const lastChat = chats[chats.length - 1];
      if (lastChat.role === "assistant" && lastChat.latest_message) {
        handleTextDelay(lastChat.message);
      } else {
        // Immediately show full message if not latest assistant message
        setDisplayedText(lastChat.message);
      }
    }
  }, [chats, handleTextDelay]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && textDelayTimeout.current) {
        clearTimeout(textDelayTimeout.current);
        setIsContentRendering(false);
      } else if (!document.hidden && chats.length > 0) {
        // When returning to the tab, immediately show the full text
        const lastChat = chats[chats.length - 1];
        if (lastChat.role === "assistant" && lastChat.latest_message) {
          setDisplayedText(lastChat.message);
          setIsContentRendering(false);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (textDelayTimeout.current) {
        clearTimeout(textDelayTimeout.current);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [chats]);

  const handleChatResponse = async ({ text, value, media = [] } = {}) => {
    let tempSessionId = selectedChat?._id || isSelectedSessionId?.current?._id;
    setTempId(selectedChat?._id || isSelectedSessionId?.current?._id);

    const tempUserMessage = {
      role: "user",
      message: text || inputText,
      sessionId: tempSessionId || `temp-user-${Date.now()}`,
      ...(media.length > 0 && { media: media.map((file) => file.fileUrl) }),
      doc: media.length > 0 ? `temp-user-${Date.now()}` : null,
    };

    dispatch(setQueryMessage(tempUserMessage));
    dispatch(setChatSessionId(selectedChat?._id));

    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    }, 100);

    try {
      setLoading(true);

      const textInput = text || inputText;

      if (!textInput.trim()) {
        return;
      }

      setInputText("");

      const userMessage = {
        role: "user",
        message: textInput,
        _id: `temp-user-${Date.now()}`,
        ...(media.length > 0 && { media: media.map((file) => file.fileUrl) }),
        doc: media.length > 0 ? `temp-user-${Date.now()}` : null,
        sessionId: tempSessionId || `temp-user-${Date.now()}`,
      };

      setChats((prev) => [...prev, userMessage]);
      trackEvent('DeepSearch_UserMessageSent', { message: textInput, sessionId: tempSessionId });
      const payload = {
        message: textInput,
        ...((selectedChat?._id || isSelectedSessionId?.current?._id) && {
          sessionId: selectedChat._id || isSelectedSessionId?.current?._id,
        }),
      };

      const res = await createDeepSearchChat(payload);
      const { data } = res;

      // Add loading message with progress bar
      const loadingMessage = {
        role: "assistant",
        message: "Processing your request...",
        _id: `loading-${Date.now()}`,
        type: "loading",
        progress: 0,
      };
      setChats((prev) => [...prev, loadingMessage]);

      // Start polling for status
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await getDeepSearchChatStatus(data?.userChat?._id);
          const statusData = statusRes.data;
          if (statusData?.status === "completed") {
            // Update the loading message with the final response
            const finalMessage = {
              role: "assistant",
              message:
                statusData?.assistantMessage?.deepSearch?.output ||
                "No response received.",
              _id: `bot-${Date.now()}`,
              sessionId: statusData?.assistantMessage?.sessionId,
              isAudio: statusData?.assistantMessage?.isAudio || false,
              isDownloadable:
                statusData?.assistantMessage?.isDownloadble || false,
              fileDownloadLink: statusData?.assistantMessage?.outputUrl || "",
              annotations:
                statusData?.assistantMessage?.deepSearch?.annotations,
              visitedURLs:
                statusData?.assistantMessage?.deepSearch?.visitedURLs,
            };

            setChats((prev) => {
              const updatedChats = prev.map((chat) =>
                chat._id === loadingMessage._id ? finalMessage : chat
              );
              return updatedChats;
            });
            setDisplayedText(finalMessage.message);
            trackEvent('DeepSearch_AssistantResponseReceived', { response: finalMessage.message, sessionId: finalMessage.sessionId });
            clearInterval(pollInterval);
          }
        } catch (error) {
          console.error("Error fetching chat status:", error);
          trackException(error); 
          clearInterval(pollInterval);
        }
      }, 15000); // Poll every 15 seconds
    } catch (error) {
      const errorBotMessage = {
        role: "assistant",
        message: error?.message || "Something went wrong!!",
        _id: `error-bot-${Date.now()}`,
        statusCode: "error",
      };
      trackException(error)
      setChats((prev) => [...prev, errorBotMessage]);
    } finally {
      setLoading(false);
    }
  };

  const isMicOn = useSelector((state) => state.voiceMode.isMicOn);
  const isMicOnRef = useRef(isMicOn);
  const isSelectedSessionId = useRef(selectedChat);

  useEffect(() => {
    isSelectedSessionId.current = selectedChat;
  }, [selectedChat]);

  useEffect(() => {
    isMicOnRef.current = isMicOn;
  }, [isMicOn]);

  // Add scroll to bottom function
  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  return (
    <div
      className={`flex-1 flex flex-col overflow-scroll sm:overflow-hidden  ${
        isMobile && sidebarOpen ? "opacity-50" : ""
      }`}
    >
      {!isLoadingHistory && !selectedChat?._id && chats?.length === 0 && (
        <WelcomeInterface handleChatResponse={handleChatResponse} />
      )}

      {isLoadingHistory ? (
        <SkeletonLoader />
      ) : chats?.length > 0 ? (
        <div
          id="scrollableDiv"
          ref={scrollRef}
          className="flex-1 py-4 !px-[30px] sm:px-[50px] md:px-[20px] lg:px-[100px] xl:!px-[220px] 2xl:!px-[220px] overflow-y-auto flex flex-col-reverse mt-[40px]"
        >
          <button
            onClick={scrollToBottom}
            className="fixed bottom-36 right-7 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-all duration-200 z-20"
          >
            <ChevronDown className="w-5 h-5 text-gray-600" />
          </button>
          <InfiniteScroll
            inverse={true} // Key for chat-style
            dataLength={chats.length}
            next={fetchMoreData}
            hasMore={hasMore}
            scrollableTarget="scrollableDiv"
            loader={null}
            endMessage={
              chats?.totalPages >= 2 ? (
                <p className="text-center m-2 font-segoeUI">No More chats!</p>
              ) : (
                ""
              )
            }
            scrollThreshold="200px"
            className="overflow-hidden"
          >
            {chats.map((chat, i) =>
              chat.role === "user" ? (
                <div
                  key={chat._id || `chat-${i}`}
                  className={`flex group justify-end ${
                    i === 0 ? "mt-12 mb-8 " : "mt-8 mb-8"
                  }`}
                >
                  <div className="flex flex-row gap-2">
                    <div className="flex flex-col ">
                      <div className="bg-[#1b5c2926] text-[#000000] flex flex-col gap-2 overflow-x-auto rounded-bl-[16px] rounded-br-[16px] rounded-tl-[16px] p-[14px] min-h-[44px] justify-center w-fit group">
                        <div
                          className="markDownWrapper !leadings-[27px] font-hel leading-[27px] w-fit whitespace-pre-line break-words font-medium text-sm "
                        >
                          <Markdown
                            key={`chat-${chat?._id}`}
                            options={{
                              overrides: {
                                a: { component: CustomLink },
                              },
                            }}
                          >
                            {preprocessText(chat.message)}
                          </Markdown>
                        </div>
                        {chat.media && chat.media.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {chat.media.map((mediaItem, fileIndex) => (
                              <FileCard
                                index={i}
                                key={`filecard-${mediaItem}`}
                                mediaItem={mediaItem}
                                fileIndex={fileIndex}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <CopyToClipboard message={chat.message} id={chat._id} />
                        {chat?.isAudio && (
                          <div className="p-1 ">
                            <Mic className="text-[#000000B2]" size={16} />
                          </div>
                        )}
                      </div>
                    </div>
                    {!isMobile && (
                      <div className="!w-[35px] flex-shrink-0">
                        <img
                          src={Avatar}
                          alt="Dabur Logo"
                          className="w-[40px]"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                ((selectedChat?._id && selectedChat?._id === chat.sessionId) ||
                  !selectedChat?._id) && (
                  <div key={chat._id} className="mt-8 mb-8 max-w-4xl space-y-8">
                    <div className="flex gap-4 w-[280px] sm:w-full">
                      {!isMobile && (
                        <div className="w-[40px]">
                          <img
                            src={daburLogo}
                            alt="Dabur Logo"
                            className="w-[40px]"
                          />
                        </div>
                      )}
                      <div className="text-[#000000] flex flex-col gap-2 overflow-x-auto rounded-bl-lg rounded-br-lg rounded-tr-lg min-h-[44px] justify-center w-fit group">
                        {chat.type === "loading" ? (
                          // <div className="p-4 border-0  !border-[#1B5C29] rounded-xl bg-white">
                          //   <p className="mb-2 text-base font-medium text-gray-600">
                          //     <RotatingTextLoader messages={[
                                // "Analyzing query parameters",
                                // "Searching internal databases",
                                // "Retrieving document context",
                                // "Synthesizing findings",
                                // "Finalizing insights"
                          //     ]} />
                          //   </p>
                          //   <DaburProgressBar progress={chat.progress} />
                          // </div>
                           <span className="p-2">
                                                <RotatingTextLoader messages={[
                                                        "Analyzing query parameters",
                                "Searching internal databases",
                                "Retrieving document context",
                                "Synthesizing findings",
                                "Finalizing insights"
                                                ]} />
                                              </span>
                        ) : (
                          <div
                            className="markDownWrapper !leadings-[27px] font-hel w-fit whitespace-pre-line break-words font-medium text-md"
                          >
                            <DeepSearchBotMarkdown
                              content={deepSearchPreprocessText(
                                chat.message ?? displayedText,
                                chat?.annotations
                              )}
                              annotations={chat?.annotations || []}
                              visitedURLs={chat?.visitedURLs || []}
                            />
                            {/* <BotMarkdown
                              content={
                                chat?.latest_message && i === chats.length - 1
                                  ? preprocessText(
                                      chat?.isAudio
                                        ? chat?.message
                                        : displayedText
                                    )
                                  : preprocessText(chat.message)
                              }
                            /> */}
                            <CopyToClipboard
                              message={chat.message}
                              id={chat._id}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              )
            )}
          </InfiniteScroll>

          {isLoadingMore && !isLoadingHistory && (
            <div className="flex justify-center items-center gap-2 my-4 py-2 w-full">
              <svg
                className="animate-spin"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#1B5C29"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              <span className="text-sm text-[#1B5C29] font-medium">Loading more...</span>
            </div>
          )}
        </div>
      ) : selectedChat?._id ? (
        <SkeletonLoader />
      ) : (
        <></>
      )}

      {/* Text Input and File Uploading */}
      <div
        className={classNames(
          "sticky shadow-[0_-4px_20px_rgba(0,0,0,0.08)] bg-[#ECF2EE] bottom-0 mb-0 py-4 !px-[30px] sm:!px-[150px] md:!px-[20px] lg:!px-[200px] xl:!px-[200px] 2xl:!px-[200px]",
          {
            "opacity-50 pointer-events-none":
              loading && selectedChat?._id === tempId,
          }
        )}
      >
        <div
          className="relative w-full !h-[72px] rounded-[14px] !shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]
        "
        >
          <TextInput
            inputRef={inputRef}
            inputText={inputText}
            handleChatResponse={handleChatResponse}
            setInputText={setInputText}
            inputFocused={inputFocused}
            setInputFocused={setInputFocused}
            loading={loading}
            disabled={loading && selectedChat?._id === tempId}
            chats={chats}
            isContentRendering={isContentRendering}
            hasDocument={hasDocument}
            isMultipleFileUploadEnabled={true}
            isFileUploadOptionVisible={false}
          />
        </div>
        <div className="max-w-4xl mx-auto mt-[40px]">
          <p className="text-xs text-gray-500 text-center">
          {MESSAGES.POWERED_BY}
          </p>
        </div>
      </div>
    </div>
  );
}

DeepSearchChatInterface.propTypes = {
  isMobile: PropTypes.bool,
  sidebarOpen: PropTypes.bool,
  setChats: PropTypes.func,
  chats: PropTypes.array,
};
